import { useMemo } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { getLocaleStrings, t, LocaleStrings, LocaleKey } from '../common/localization';

/**
 * Hook that provides localized strings based on the current language.
 * Re-renders automatically when the user changes language.
 *
 * Usage:
 *   const { s, t } = useLocale();
 *   // Direct access:  s.nav_stories
 *   // With params:    t('btn_load_more_games', { count: 5 })
 */
export function useLocale() {
  const { language } = useLanguage();

  const strings = useMemo(() => getLocaleStrings(language), [language]);

  const translate = useMemo(() => {
    return (key: LocaleKey, params?: Record<string, string | number>) =>
      t(strings, key, params);
  }, [strings]);

  return {
    /** Full locale strings object – use for simple key access: s.nav_stories */
    s: strings,
    /** Translate with interpolation: t('btn_load_more_games', { count: 5 }) */
    t: translate,
  };
}
